import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useI18n } from "../../../i18n/useI18n";
import Drawer from "@/components/UI/Drawer/Drawer";
import Button from "@/components/UI/Button/Button";
import { Loader } from "../../../users/components/Loader";
import { ErrorMessage } from "../../../../components/Notification/ErrorMessage";
import { IFormData, IProjectsData } from "../../../../api/projects/types";
import { useCreateProject } from "../../hooks/useCreateProject";
import { useUpdateProject } from "../../hooks/useUpdateProject";
import { useGetAllCompanies } from "@/features/users/hooks/useGetAllCompanies";
import { ICompany } from "@/features/companies/slices/companiesSlice";
import formStyles from '@/features/users/styles/userForm.module.scss';

interface ProjectFormDrawerProps {
  projectData?: IProjectsData;
  isOpen: boolean;
  onClose: () => void;
  refetchProjects: () => void;
}

const ProjectFormDrawer = ({ projectData, isOpen, onClose, refetchProjects }: ProjectFormDrawerProps) => {
  const { t } = useI18n();
  const { data: companiesData } = useGetAllCompanies();
  const { mutateAsync: createProject, isPending: isCreating } = useCreateProject();
  const { mutateAsync: updateProject, isPending: isUpdating } = useUpdateProject();
  const [errorMessage, setErrorMessage] = useState("");
  const isPending = isCreating || isUpdating;

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<IFormData>();

  useEffect(() => {
    if (isOpen && projectData) {
      reset({
        name: projectData.name || "",
        description: projectData.description || "",
        startDate: projectData.startDate ? new Date(projectData.startDate).toISOString().slice(0, 16) : "",
        endDate: projectData.endDate ? new Date(projectData.endDate).toISOString().slice(0, 16) : "",
        idStatus: projectData.idStatus || 1,
        idCompany: projectData.idCompany?.toString() || ""
      });
    } else {
      reset({ name: "", description: "", startDate: "", endDate: "", idCompany: "" });
    }
  }, [projectData, isOpen, reset]);

  const onSubmit = async (data: IFormData) => {
    setErrorMessage("");
    const dataToSave = { ...data, idCompany: Number(data.idCompany) };
    
    try {
      if (projectData?.id) {
        await updateProject({ projectId: projectData.id, formData: dataToSave });
      } else {
        await createProject(dataToSave);
      }
      refetchProjects();
      handleClose();
    } catch (error: unknown) {
      const err = error as Error;
      setErrorMessage(err.message || "Ocurrió un error inesperado.");
    }
  };
  
  const handleClose = () => {
    reset();
    setErrorMessage("");
    onClose();
  }

  return (
    <Drawer isOpen={isOpen} onClose={handleClose}>
      <form onSubmit={handleSubmit(onSubmit)} style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Drawer.Header onClose={handleClose}>
          {projectData ? t("module_projects.modal_update_project_title") : t("module_projects.modal_create_project_title_new")}
        </Drawer.Header>
        <Drawer.Body>
          {isPending && <Loader />}
          <div className={formStyles.formGrid}>
            <div className={`${formStyles.formField} ${formStyles.fullWidth}`}>
              <label htmlFor="name" className={formStyles.label}>Nombre del Proyecto</label>
              <input
                id="name"
                className={formStyles.input}
                {...register("name", { required: "El nombre es obligatorio" })}
                placeholder="Escribe un nombre para el proyecto..."
              />
              {errors.name && <p className={formStyles.errorMessage}>{errors.name.message}</p>}
            </div>

            <div className={`${formStyles.formField} ${formStyles.fullWidth}`}>
              <label htmlFor="description" className={formStyles.label}>Descripción</label>
              <textarea
                id="description"
                className={`${formStyles.input} ${formStyles.textarea}`}
                {...register("description", { required: "La descripción es obligatoria" })}
                placeholder="Añade una descripción detallada..."
              />
              {errors.description && <p className={formStyles.errorMessage}>{errors.description.message}</p>}
            </div>

            <div className={formStyles.formField}>
              <label htmlFor="startDate" className={formStyles.label}>Fecha de Inicio</label>
              <input
                type="datetime-local"
                id="startDate"
                className={formStyles.input}
                {...register("startDate", { required: "La fecha de inicio es obligatoria" })}
              />
              {errors.startDate && <p className={formStyles.errorMessage}>{errors.startDate.message}</p>}
            </div>
            <div className={formStyles.formField}>
              <label htmlFor="endDate" className={formStyles.label}>Fecha de Fin</label>
              <input
                type="datetime-local"
                id="endDate"
                className={formStyles.input}
                {...register("endDate", { required: "La fecha de fin es obligatoria" })}
              />
              {errors.endDate && <p className={formStyles.errorMessage}>{errors.endDate.message}</p>}
            </div>
          
            <div className={`${formStyles.formField} ${formStyles.fullWidth}`}>
              <label htmlFor="idCompany" className={formStyles.label}>{t("module_users.modal_create_user_company")}</label>
              <select
                id="idCompany"
                className={formStyles.select}
                {...register("idCompany", { required: "Debes seleccionar una compañía" })}
              >
                <option value="">Selecciona una compañía</option>
                {companiesData?.data?.map((c: ICompany) => (
                  <option key={c.id} value={c.id}>{c.nameCompany}</option>
                ))}
              </select>
              {errors.idCompany && <p className={formStyles.errorMessage}>{errors.idCompany.message}</p>}
            </div>
          </div>
          {errorMessage && <ErrorMessage message={errorMessage} />}
        </Drawer.Body>
        <Drawer.Footer>
          <Button variant="secondary" onClick={handleClose}>Cancelar</Button>
          <Button variant="primary" type="submit" disabled={isPending}>
            {isPending ? "Guardando..." : (projectData ? "Actualizar" : "Crear")}
          </Button>
        </Drawer.Footer>
      </form>
    </Drawer>
  );
};

export default ProjectFormDrawer; 